import React, { useState } from 'react';
import { dayjs } from 'shared/utils/date';
import { useMutation } from 'hooks/apollo';
import { gql } from '@apollo/client';
import { FormattedMessage, useIntl } from 'react-intl';
import { EDIT_REPORT } from 'shared/constants/permissions';
import LazyLoad from 'react-lazyload';

import ResultContent from 'containers/Results/ResultsPage/ResultContent';
import Drawer from 'components/Drawer';
import Modal from 'components/Modal';
import ChartHeader from 'components/Charts/ChartHeader';
import ErrorBoundary from 'components/ErrorBoundary';
import { Form, Empty, Card } from 'antd';

import useCustomer from 'hooks/useCustomer';
import { isDateAfterDateRangeStart } from 'shared/constants/date';
import useGlobalContext from 'hooks/useGlobalContext';
import useDataSource from 'hooks/useDataSource';
import { toAddToReportInput } from '../mapper';
import QuestionComparison from './QuestionComparison';
import AddToReportForm from './AddToReport';
import messages from './messages';

const ADD_TO_REPORT = gql`
  mutation ($input: ReportContentInput!) {
    createReportContent (input: $input) {
      id
    }
  }
`;

const ResultsPage = ({
  surveyGroupId,
  displayedSurveyId,
  dateRange,
  filterSegments,
  benchmarkSegments,
  content = [],
  onEdit,
  emptyMessage,
  allowPersonalData,
  onSelectChartSegment,
}) => {
  const { formatMessage: $t } = useIntl();
  const { message } = useGlobalContext();
  const { customer, hasCustomerPermission } = useCustomer();
  const { dataSourceId } = useDataSource();
  const [addToReportData, setAddToReportData] = useState(null);
  const [addToReportForm] = Form.useForm();
  const [addToReport, { loading: adding }] = useMutation(ADD_TO_REPORT);
  const [questionToCompare, setQuestionToCompare] = useState(null);
  const closeDrawer = () => setAddToReportData(null);

  const addChartToReport = async (reportTitle, reportId, chartComment) => {
    const input = toAddToReportInput({
      reportId,
      chartComment,
      addToReportData,
      surveyGroupId,
      dataSourceId,
      filterSegments,
      benchmarkSegments,
    });
    try {
      await addToReport({ variables: { input } });
      closeDrawer();
      message.success($t(messages.addToReportSuccess, { title: reportTitle }));
    } catch (error) {
      console.error(error);
      message.error($t(messages.addToReportError, { title: reportTitle }));
    }
  };

  const activeContent = content.filter(c => c.active !== false || isDateAfterDateRangeStart(dayjs, dateRange, c.latestResponse));

  if (!activeContent.length) {
    return <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={emptyMessage} />;
  }

  return (
    <div className="results-page">
      <Modal
        title={<FormattedMessage {...messages.crossTabulate} />}
        open={!!questionToCompare}
        onCancel={() => setQuestionToCompare(null)}
        fullWidth
        centered
        maskClosable={false}
        footer={null}
        zIndex={999} // add to report drawer should be above
      >
        {questionToCompare && (
        <QuestionComparison
          question={questionToCompare}
          surveyGroupId={surveyGroupId}
          displayedSurveyId={displayedSurveyId}
          filterSegments={filterSegments}
          dateRange={dateRange}
          addToReport={hasCustomerPermission(EDIT_REPORT) && setAddToReportData}
          onSelectChartSegment={onSelectChartSegment}
          dataSourceId={dataSourceId}
        />
        )}
      </Modal>
      <Drawer
        title={<FormattedMessage {...messages.addToReportDrawerTitle} />}
        open={!!addToReportData}
        onClose={closeDrawer}
        onCancel={closeDrawer}
        onOk={addToReportForm.submit}
        confirmLoading={adding}
      >
        <AddToReportForm customerId={customer?.id} form={addToReportForm} onSubmit={addChartToReport} />
      </Drawer>
      {activeContent.map((item, i) => (
        <Card key={i}>
          <ChartHeader title={item.title} translate={item.translateTitle} active={item.active} />
          <LazyLoad height={382} offset={100} scrollContainer=".results-page">
            <ErrorBoundary>
              <ResultContent
                wrapperClass=""
                hideTitle
                dateRange={dateRange}
                surveyGroupId={surveyGroupId}
                displayedSurveyId={displayedSurveyId}
                filterSegments={filterSegments}
                benchmarkSegments={benchmarkSegments}
                contentSettings={item.contentSettings}
                addToReport={hasCustomerPermission(EDIT_REPORT) && setAddToReportData}
                compare={setQuestionToCompare}
                onEdit={onEdit}
                allowPersonalData={allowPersonalData}
                onSelectChartSegment={onSelectChartSegment}
                dataSourceId={dataSourceId}
              />
            </ErrorBoundary>
          </LazyLoad>
        </Card>
      ))}
    </div>
  );
};

export default ResultsPage;
